import {
  ReactNode,
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import ErrorBoundary from "components/error-boundary";
import { importWithRetry, lazyWithRetry } from "utils/lazy-with-retry.util";

import type { FlyoutCardHandle } from "./side-menu-flyout-card.component";

/** What the card says about the row under the pointer. */
export type SideMenuFlyoutContent = {
  label: string;
  /** The row's group ("Front desk"), shown small above the label. */
  eyebrow?: string;
  /** One plain sentence: what the page is for. */
  description?: string;
  /** A count (every menu badge is one); drawn as text, "99+" past 99. */
  badgeContent?: number | string;
  /** The row is the current page: the card carries the active accent. */
  active?: boolean;
  /** Suno-staff rows (Administration) keep their amber accent on the card. */
  staff?: boolean;
  /** Clicking the card does what clicking the row does. */
  onActivate?: () => void;
};

type FlyoutApi = {
  show: (anchor: HTMLElement, content: SideMenuFlyoutContent) => void;
  /** Hides after a short grace, so the pointer can cross the gap onto the card. */
  hide: () => void;
  /** Hides now: the list scrolled or the rail expanded under it. */
  hideNow: () => void;
};

const SideMenuFlyoutContext = createContext<FlyoutApi | null>(null);

/** `null` outside a side menu. Rows use it only in the rail; expanded they keep their tooltip. */
export const useSideMenuFlyout = () => useContext(SideMenuFlyoutContext);

/** The gap between the rail's edge and the card, bridged by the card's notch. */
const FLYOUT_GAP_PX = 10;
/** Long enough to cross the gap onto the card, short enough to feel instant. */
const HIDE_GRACE_MS = 140;

type Placement = { top: number; left: number };

export type FlyoutState = {
  placement: Placement;
  content: SideMenuFlyoutContent;
  /** False for the first card after a pause: it fades in rather than gliding. */
  glide: boolean;
  /** The provider's `resetKey` when it opened; any other key means it is stale. */
  openedFor: unknown;
};

/**
 * Loaded on demand, and never with the reload rung: a hover card that fails to
 * load is not worth reloading someone's page over, so it renders nothing.
 */
const SideMenuFlyoutCard = lazyWithRetry(
  () => import("./side-menu-flyout-card.component"),
  "side-menu-flyout-card",
  { reloadFallback: false }
);

/** The same chunk as the lazy card above, fetched ahead of the first hover. */
const warmFlyoutCard = () =>
  importWithRetry(
    () => import("./side-menu-flyout-card.component"),
    "side-menu-flyout-card"
  );

function Nothing() {
  return null;
}

/**
 * Fetches the card's chunk once the browser is idle, then reports it ready, so
 * the card can be mounted (hidden) before anyone hovers.
 */
function warmWhenIdle(load: () => Promise<unknown>, onReady: () => void) {
  let cancelled = false;
  const run = () => {
    load()
      .then(() => {
        if (!cancelled) onReady();
      })
      .catch(() => undefined);
  };
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(run, { timeout: 3000 });
    return () => {
      cancelled = true;
      window.cancelIdleCallback(id);
    };
  }
  const id = window.setTimeout(run, 1500);
  return () => {
    cancelled = true;
    window.clearTimeout(id);
  };
}

/**
 * The rail's hover card (ENG-2535 follow-up). One card for the whole rail, not a
 * tooltip per row: it opens with no delay, and moving down the rail glides it from
 * row to row instead of closing one tooltip and opening the next. It is drawn in
 * the menu's own plum so it reads as the row growing out of the rail, and it is a
 * target in its own right — clicking the label goes where the icon goes, which is
 * what a person aiming at a word expects.
 *
 * Decorative for assistive tech (`aria-hidden`): every rail row already carries
 * its full name as `aria-label`.
 */
export function SideMenuFlyoutProvider({
  resetKey,
  children
}: {
  /** Any change closes the card: the menu expanded or collapsed under it. */
  resetKey: unknown;
  children: ReactNode;
}) {
  // Once the card's chunk is loaded the card stays mounted, hidden between
  // hovers. Nothing about a hover lives in React state: `show` and `hide` write
  // to the card directly, so hovering down the rail renders nothing.
  const [mounted, setMounted] = useState(false);
  const cardRef = useRef<FlyoutCardHandle>(null);
  // A hover that came before the card had loaded, for the card to show on mount.
  const pendingRef = useRef<FlyoutState | null>(null);
  const contentRef = useRef<SideMenuFlyoutContent | null>(null);
  const openedForRef = useRef<unknown>(undefined);
  const hideTimer = useRef<number | undefined>(undefined);
  // Whether a card is up: `hideNow` runs on every scroll event and must cost
  // nothing while no card is showing.
  const openRef = useRef(false);

  const cancelHide = useCallback(() => {
    window.clearTimeout(hideTimer.current);
    hideTimer.current = undefined;
  }, []);

  const close = useCallback(() => {
    openRef.current = false;
    contentRef.current = null;
    pendingRef.current = null;
    cardRef.current?.hide();
  }, []);

  const hideNow = useCallback(() => {
    if (!openRef.current) return;
    cancelHide();
    close();
  }, [cancelHide, close]);

  const hide = useCallback(() => {
    cancelHide();
    hideTimer.current = window.setTimeout(close, HIDE_GRACE_MS);
  }, [cancelHide, close]);

  const show = useCallback(
    (anchor: HTMLElement, content: SideMenuFlyoutContent) => {
      cancelHide();
      const rect = anchor.getBoundingClientRect();
      const menuRight =
        anchor
          .closest<HTMLElement>("[data-testid='layout-side-menu']")
          ?.getBoundingClientRect().right ?? rect.right;
      const state: FlyoutState = {
        placement: {
          top: rect.top + rect.height / 2,
          left: menuRight + FLYOUT_GAP_PX
        },
        content,
        // Glide from the last row only if a card is up, and it was opened in
        // this same menu state (not before the menu toggled under it).
        glide: openRef.current && openedForRef.current === resetKey,
        openedFor: resetKey
      };
      openRef.current = true;
      openedForRef.current = resetKey;
      contentRef.current = content;
      if (cardRef.current) {
        cardRef.current.show(state);
      } else {
        pendingRef.current = state;
        setMounted(true);
      }
    },
    [cancelHide, resetKey]
  );

  // The menu expanded or collapsed: a card still up points at a row that moved.
  // This writes to the card only; it sets no state.
  useEffect(() => {
    openRef.current = false;
    contentRef.current = null;
    pendingRef.current = null;
    cardRef.current?.hide();
  }, [resetKey]);

  useEffect(() => cancelHide, [cancelHide]);
  useEffect(() => warmWhenIdle(warmFlyoutCard, () => setMounted(true)), []);

  const takePending = useCallback(() => pendingRef.current, []);
  const activate = useCallback(() => {
    const onActivate = contentRef.current?.onActivate;
    hideNow();
    onActivate?.();
  }, [hideNow]);

  const api = useMemo(() => ({ show, hide, hideNow }), [show, hide, hideNow]);

  return (
    <SideMenuFlyoutContext.Provider value={api}>
      {children}
      {mounted && (
        <ErrorBoundary FallbackComponent={<Nothing />}>
          <Suspense fallback={null}>
            <SideMenuFlyoutCard
              ref={cardRef}
              gapPx={FLYOUT_GAP_PX}
              takePending={takePending}
              onPointerEnter={cancelHide}
              onPointerLeave={hide}
              onActivate={activate}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </SideMenuFlyoutContext.Provider>
  );
}
